import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { DataLoading } from '@pga/pga-component-library'
import { Button } from '../../Button'
import { getAnnouncementQuery } from '../../queries/announcement'
import NoAnnouncements from '../Announcements/NoAnnouncements'
import { ErrorTitle } from '../Announcements/ErrorLoading'
import {
  Container,
  Header,
  HeaderLabel,
  Divider,
  AnnouncementsList,
  AnnouncementItem,
  AnnouncementTitle,
  AnnouncementBody,
  AnnouncementLink,
  ViewMoreLink,
  ViewMoreText,
  LoadingCard,
  ErrorContainer
} from './Styles/Announcements.styles'

const ANNOUNCEMENT_COUNT = 3

const LoadingPlaceholder = () => (
  <LoadingCard>
    <DataLoading h={40} />
    <DataLoading h={40} />
    <DataLoading h={40} />
    <DataLoading h={40} />
    <DataLoading h={40} />
  </LoadingCard>
)

const ErrorState = ({ refetch }) => (
  <ErrorContainer>
    <ErrorTitle>Failed to load</ErrorTitle>
    <Button onClick={() => refetch()}>REFRESH</Button>
  </ErrorContainer>
)

const Announcements = ({ 
  mainProgramTypeCode, 
  sectionName,
  authReady 
}) => {
  const { data, loading, error, refetch } = useQuery(getAnnouncementQuery, {
    variables: {
      group: mainProgramTypeCode,
      section: sectionName,
      limit: ANNOUNCEMENT_COUNT
    },
    skip: !authReady || !mainProgramTypeCode,
    fetchPolicy: 'cache-and-network'
  })

  const announcements = useMemo(() => 
    data?.announcements?.map((announcement, index) => ({
      id: String(index + 1),
      title: announcement.title || 'Announcement',
      body: announcement.message || '',
      message: announcement.message || '',
      link: announcement.link,
      label: announcement.label
    })) || [],
    [data]
  )

  const newCount = announcements.filter(a => a && a.isNew).length

  if (loading) {
    return <LoadingPlaceholder />
  }

  if (error) {
    return (
      <LoadingCard>
        <ErrorState refetch={refetch} />
      </LoadingCard>
    )
  }

  return (
    <Container>
      <Header>
        <HeaderLabel>Announcements</HeaderLabel>
        {newCount > 0 && (
          <>
            <Divider />
            <HeaderLabel>{newCount} New</HeaderLabel>
          </>
        )}
      </Header>

      {announcements.length === 0 ? (
        <NoAnnouncements />
      ) : (
        <>
          <AnnouncementsList>
            {announcements.slice(0, 2).map((announcement, index) => {
              if (!announcement) return null

              return (
                <AnnouncementItem key={announcement.id || index} withAction={index === 1}>
                  <AnnouncementTitle>{announcement.title || 'Announcement'}</AnnouncementTitle>
                  <AnnouncementBody>
                    {announcement.body || announcement.message || ''}
                    {index === 1 && announcement.body && announcement.body.length > 150 && (
                      <>
                        ... <AnnouncementLink>Read More</AnnouncementLink>
                      </>
                    )}
                  </AnnouncementBody>
                  {announcement.link && (
                    <AnnouncementLink>{announcement.label || 'Learn More'}</AnnouncementLink>
                  )}
                </AnnouncementItem>
              )
            })}
          </AnnouncementsList>

          <ViewMoreLink>
            <ViewMoreText>View More Announcements</ViewMoreText>
            <i className='fa fa-angle-right' style={{ color: '#0082CE' }} />
          </ViewMoreLink>
        </>
      )}
    </Container>
  )
}

export default Announcements
